import { useAtomValue } from "@effect/atom-react";
import { queryAtomEffect } from "@crm/shared/query-atom/effect";
import { rpcEffectCaller } from "@crm/rpc-client";
import { ReviewCoursesRpc } from "@crm/rpc-contract/groups/review-courses";
import type { GetLessonProgressInput } from "../../domain/review-course-inputs";

// 类型化 RPC Effect 调用器（错误通道保留）
const reviewCoursesRpc = rpcEffectCaller(ReviewCoursesRpc);

// Re-export for convenience
export type { CourseProgressItem } from "./progress-read-queries";

/**
 * 课时进度查询域（对应 lessonProgressKeys，Effect-native）。
 *
 * 明细路径（useLessonProgress）、概览路径（useCourseProgress）与批量路径
 * （useCourseProgressBatch）共用同一响应键 `"lessonProgress"`：任一 save/clear
 * 进度 mutation 成功后经 reactivityKeys 驱动本域全部进度原子刷新。
 */

/**
 * Hook: 明细路径 — 获取当前用户的课时进度
 *
 * @param filters.lessonIds - 可选，按 lessonId 过滤
 */
const lessonProgressAtom = queryAtomEffect(
  (key: readonly [GetLessonProgressInput | undefined]) =>
    reviewCoursesRpc.getLessonProgress(key[0] ?? {}),
  { staleTime: 1 * 60 * 1000, reactivityKeys: ["lessonProgress"] }, // 1 分钟（进度数据需要更频繁更新）
);

export function useLessonProgress(filters?: GetLessonProgressInput) {
  return useAtomValue(lessonProgressAtom([filters]));
}

/**
 * Hook: 概览路径 — 获取当前用户在某课程下的进度汇总
 *
 * 返回 courseId 下所有 lesson 的 { lessonId, completed, progressPercentage }
 *
 * @param courseId - 课程 ID
 */
const courseProgressAtom = queryAtomEffect(
  (key: readonly [string]) =>
    reviewCoursesRpc.getCourseProgress({
      courseId: key[0],
    }),
  { staleTime: 1 * 60 * 1000, reactivityKeys: ["lessonProgress"] },
);

export function useCourseProgress(courseId: string) {
  return useAtomValue(courseProgressAtom([courseId], !!courseId));
}

/**
 * Hook: 批量路径 — 获取当前用户在多个课程下的进度概览（消除 N+1）
 *
 * @param courseIds - 课程 ID 列表；空列表时折叠为禁用（对应 `enabled`）
 */
const courseProgressBatchAtom = queryAtomEffect(
  (key: readonly [readonly string[]]) =>
    reviewCoursesRpc.getCourseProgressBatch({
      courseIds: [...key[0]],
    }),
  { staleTime: 1 * 60 * 1000, reactivityKeys: ["lessonProgress"] },
);

export function useCourseProgressBatch(courseIds: string[]) {
  return useAtomValue(courseProgressBatchAtom([courseIds], courseIds.length > 0));
}
